import classNames from "classnames";
import { DropOverMenu } from "../../../DropOverMenu/DropOverMenu";
import {
	generateTrackingId,
	setTrackingId
} from "client/Misc/utils";
import {
	CSSProperties,
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { concat, range } from "client_legacy/ndaw/arrayHelpers";
import { ButtonPopupControl } from "../ButtonPopupControl";
import styles from "./InsertTableControl.module.less";
import { localize } from "client_legacy/ndaw/culture";

export type InsertTableControlProps = {
	onInsertTable(rows: number, columns: number): void;
	trackingId: string;
	isDisabled?: boolean;
	tooltip: string;
};

export const InsertTableControl = (props: InsertTableControlProps): ReactElement => {
	const {
		onInsertTable,
		isDisabled = false,
		trackingId,
		tooltip
	} = props;

	return (
		<ButtonPopupControl
			icon="grid-2-plus"
			trackingId={trackingId}
			isDisabled={isDisabled}
			tooltip={tooltip}
		>
			{({ target, onClose }): ReactElement => (
				<DropOverMenu
					target={target}
					onClose={onClose}
					title={localize("_InsertTable")}
					trackingId={generateTrackingId(trackingId, "__wrapper")}
				>
					<SizePicker
						maxSize={{rows: 8, columns: 8}}
						onPick={(rows: number, columns: number): void => {
							onClose();
							onInsertTable(rows, columns);
						}}
						trackingId={generateTrackingId(trackingId, "__size-picker")}
					/>
				</DropOverMenu>
			)}
		</ButtonPopupControl>
	);
};
InsertTableControl.displayName = "InsertTableControl";

export type SizePickerProps = {
	onPick(rows: number, columns: number): void;
	maxSize: TableSize;
	trackingId?: string;
};

export const SizePicker = (props: SizePickerProps): ReactElement => {
	const {
		maxSize,
		onPick,
		trackingId
	} = props;

	const [value, setValue] = useState<TableSize>({
		rows: 0,
		columns: 0
	});

	const matrix = useMemo(
		(): readonly [number, number][] => {
			return concat(
				range(maxSize.rows)
					.map(row => range(maxSize.columns)
						.map((col): [number, number] => [row, col]))
			);
		},
		[maxSize.columns, maxSize.rows]
	);

	const enterHandler = useCallback(
		(row: number, column: number): void => {
			setValue({
				rows: row,
				columns: column
			});
		},
		[]
	);

	const variableStyles = useMemo(
		(): CSSProperties => {
			return {
				"--rows-count": maxSize.rows,
				"--columns-count": maxSize.columns
			};
		},
		[maxSize.columns, maxSize.rows]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			style={variableStyles}
			className={styles.sizePicker}
		>
			{matrix.map(x => (
				<SizePickerCell
					key={`${x[0]}_${x[1]}`}
					row={x[0]}
					column={x[1]}
					isActive={x[0] < value.rows && x[1] < value.columns}
					onEnter={enterHandler}
					onClick={onPick}
				/>
			))}
		</div>
	);
};
SizePicker.displayName = "SizePicker";

export type SizePickerCellProps = {
	row: number;
	column: number;
	isActive: boolean;
	onEnter(row: number, column: number): void;
	onClick(row: number, column: number): void;
};

export const SizePickerCell = (props: SizePickerCellProps): ReactElement => {
	const {
		row,
		column,
		isActive,
		onEnter,
		onClick
	} = props;

	const mouseEnterHandler = useCallback(
		(): void => {
			onEnter(row + 1, column + 1);
		},
		[
			column,
			onEnter,
			row
		]
	);

	const clickHandler = useCallback(
		() => {
			onClick(row + 1, column + 1);
		},
		[
			column,
			onClick,
			row
		]
	);

	return (
		<button
			{...setTrackingId("size-picker-cell", `|${row}x${column}`)}
			className={classNames(
				styles.cell,
				{
					[styles.cell_active]: isActive
				}
			)}
			onMouseEnter={mouseEnterHandler}
			onClick={clickHandler}
			type="button"
			style={{
				gridArea: `${row + 1} / ${column + 1}`
			}}
		/>
	);
};
SizePickerCell.displayName = "SizePickerCell";

type TableSize = {
	rows: number;
	columns: number;
};